import rules from "@/utils/inputRules";
import { isFalse, isTrue } from "@/utils/service/DocumentGeneration/answers";
import { toUrl } from "@/utils/textUtilities";
import { COOKIE_POLICY, TERMS_OF_SERVICE } from "@/utils/service/documents";
import { translation } from "@/utils/service/Translations/translations";

export default [
    {
        name: "General Information",
        inputs: [
            {
                model: "company.type",
                type: "radio",
                default: "Service Provider",
                values: {
                    "Service Provider": "Individual",
                    Company: "Business",
                },
            },
            {
                model: "company.name",
                type: "businessName",
                classes: "col-md-6",
                rules: [rules.required],
            },
            {
                model: "company.email",
                type: "email",
                classes: "col-md-6",
                rules: [rules.required],
            },
            {
                label: "Correspondence address",
                model: "company.address",
                type: "text",
                classes: "col-md-6",
                rules: [rules.required],
            },
            {
                model: "website_url",
                type: "websiteUrl",
                classes: "col-md-6",
                rules: [rules.required],
            },
            {
                model: "company.phone",
                type: "phone",
                classes: "col-md-6",
                rules: [rules.required],
            },
            {
                model: "company.fax",
                type: "fax",
                classes: "col-md-6",
            },
            {
                model: "company.country",
                type: "country",
                classes: "col-md-6",
                rules: [rules.required],
            },
            {
                title:
                    "Please provide the email address for the questions related to Privacy Policy",
                model: "company.policy_email",
                type: "email",
            },
        ],
        actions: [
            {
                component: () => import("@/components/Projects/Project/ProjectGeneralInformationUpdateOnDocumentCreate.vue"),
                params: (self) => ({
                    color: "secondaryDark",
                    text: true,
                    class: "th-mr-12",
                    to: {
                        name: "project_index",
                        params: self?.$route.params,
                    },
                }),
                cta: "Edit project information",
            },
        ]
    },
    {
        name: "Terms of Services",
        title: "Do you have Terms of Services on your website?",
        inputs: [
            {
                model: "haveTOS",
                type: "radio",
                default: "false",
                values: {
                    true: "Yes",
                    false: "No",
                },
            },
            {
                condition: (model) => isTrue(model["haveTOS"]),
                title: "Please insert the link to your Terms of Service",
                model: "tos_link",
                type: "projectDocument",
                policyType: TERMS_OF_SERVICE,
            },
            {
                condition: (model) => isFalse(model["haveTOS"]),
                type: "custom",
                component: () =>
                    import("@/components/DocumentGeneration/Alert.vue"),
                input: {
                    title: `We strongly recommend that you include a Terms of Service for your website`,
                },
            },
            {
                condition: (model) => isFalse(model["haveTOS"]),
                model: "tos_no_use_create",
                type: "projectDocumentCreateSuggest",
                policyType: TERMS_OF_SERVICE,
            },
        ],
    },
    {
        name: "Data Collection",
        title: "Do you collect information provided by your user?",
        inputs: [
            {
                model: "collected_data.account_signup_info",
                type: "multiSelect",
                label: "Account sign up information",
                inline: true,
                default: [],
                values: {
                    name: "Name",
                    surname: "Surname",
                    email_address: "Email",
                    phone_number: "Phone",
                    user_name: "Username",
                    passwords: "Passwords",
                    personal_number: "Personal Number",
                },
            },
            {
                model: "collected_data.main_info",
                type: "multiSelect",
                label: "Main information",
                inline: true,
                default: [],
                values: {
                    name: "Name",
                    surname: "Surname",
                    address: "Address",
                    personal_number: "Personal Number",
                    birth_date: "Birth date",
                    documents: "Personal document numbers",
                },
            },
            {
                model: "collected_data.contact_info",
                type: "multiSelect",
                label: "Contact information",
                inline: true,
                default: [],
                values: {
                    address: "Address",
                    email_address: "Email",
                    phone_number: "Phone",
                },
            },
            {
                model: "collected_data.payment_info",
                type: "multiSelect",
                label: "Payment information",
                inline: true,
                default: [],
                values: {
                    credit_card_numbers: "Credit or debit card number",
                    credit_or_debit_card_type: "Credit or debit card type",
                    credit_or_debit_card_expiration_date:
                        "Credit or debit card expiration date",
                    billing_address: "Billing address",
                    tax_number: "Tax number",
                    name_and_surname: "Name and surname",
                },
            },
            {
                model: "collected_data.communication_data",
                type: "multiSelect",
                label: "Communication",
                inline: true,
                default: [],
                values: {
                    communication_data: "Communication, Chats, Messaging",
                },
            },
            {
                model: "collected_data.service_info",
                type: "multiSelect",
                label: "Information for service",
                inline: true,
                noTrans: true,
                default: [],
                values: [
                    "užsakymo duomenys",
                    "paskyrose ir sąskaitose esantys duomenys",
                    "duomenys susiję su atliktais mokėjimais",
                    "informacija apie klientų įsigytas prekes ar paslaugas",
                ],
            },
            {
                model: "collected_data.other_info",
                type: "addOption",
                label: "Other",
                addLabel: "Enter information you collect",
                default: [],
                options: [
                    "Mobiliojo įrenginio tipą, įrenginio nustatymus, lankytojo koordinates",
                    "Apklausų bei kitus duomenis, kuriuos Jums pateikia lankytojas",
                ],
                inline: true,
            },
        ],
    },
    {
        name: "Payments",
        inputs: [
            {
                title: "Do you accept payments in your website?",
                model: "payment_processor.use",
                type: "radio",
                default: "false",
                values: {
                    true: "Yes",
                    false: "No",
                },
            },
            {
                condition: (model) => model["payment_processor.use"] === "true",
                title:
                    "Do you use payment processor(s) (e.g. PayPal, Stripe, Braintree, Adyen) for payments processing?",
                model: "payment_processor.use_payment_processors",
                type: "radio",
                default: "false",
                values: {
                    true: "Yes",
                    false: "No",
                },
            },
            {
                condition: (model) =>
                    model["payment_processor.use"] === "true" &&
                    model["payment_processor.use_payment_processors"] ===
                        "true",
                model: "payment_processor.payment_processors",
                type: "addToList",
                default: [],
                inputs: [
                    {
                        label: "Payment processor name",
                        model: "name",
                        type: "text",
                        rules: [rules.required],
                    },
                    {
                        label: "Privacy Policy URL",
                        model: "policy_link",
                        type: "text",
                        rules: [rules.url],
                        filters: [(value) => toUrl(value)],
                    },
                ],
            },
        ],
    },
    {
        name: "Automatically Collected Data",
        title: "What information do you automatically collect?",
        inputs: [
            {
                model: "automatically_collected_data.tracking",
                type: "multiSelect",
                label: "Techical Data",
                inline: true,
                noTrans: true,
                default: [],
                values: [
                    "IP adresas",
                    "naršyklės tipas",
                    "informacija apie naudojamą operacinę sistemą",
                    "laiko/datos žymos",
                    "lankytojo veiklos informacija",
                ],
            },
            // {
            //     model: "automatically_collected_data.device_log_data",
            //     type: "multiSelect",
            //     label: "Log data and device information",
            //     inline: true,
            //     default: [],
            //     values: {
            //         ip: "Internet protocol (IP) addresses",
            //         browser_type: "Browser type",
            //         isp: "Internet service provider (ISP)",
            //         exit_pages: "Referring/exit pages",
            //         operating_system: "Operating system",
            //         time_stamp: "Date/time stamp",
            //         clickstream_data: "Clickstream data",
            //     },
            // },
            {
                model: "automatically_collected_data.other",
                type: "multiSelect",
                inline: true,
                noTrans: true,
                default: [],
                result: "object",
                values: {
                    geo_data: "Geo-lokacijos duomenys",
                    usage_info: "Svetainės naudojimosi duomenys su Google Analytics",
                    personal_info: "Viešai prieinama asmeninė informacija",
                    cookies: "Slapukų informacija",
                },
            },
            {
                model: "automatically_collected_data.other_info",
                type: "addOption",
                label: "Other",
                addLabel: "Enter information automatically collected",
                inline: true,
                noTrans: true,
                default: [],
                options: [],
            },
        ],
    },
    {
        name: "Data Usage",
        title: "How do you use information collected?",
        inputs: [
            {
                model: "info_usage",
                type: "multiSelect",
                noTrans: true,
                default: [],
                values: [
                    "vykdyti lankytojo pateiktus užsakymus;",
                    "siekiant identifikuoti lankytoją;",
                    "sukurti paskyrą;",
                    "užtikrinti Svetainės saugumą;",
                    "teikti paslaugas, sudaryti ir vykdyti sutartis; administruoti klientų duomenų bazę;",
                    "atsakyti į lankytojų klausimus, komentarus ar užklausas;",
                    "spręsti problemas, susijusias su paslaugų įgyvendinimu, pateikimu ir panaudojimu;",
                    "susisiekti su klientais, pasikeitus įsigytų paslaugų sąlygoms;",
                    "siųsti sąskaitas ir kitą su paslaugomis ir/ar prekėmis susijusią informaciją;",
                    "vertinti klientų mokumą ir valdyti galimą įsiskolinimą;",
                    "gerinti paslaugų kokybę;",
                    "kitais tikslais, susijusiais su vidaus administravimu, pvz. tvarkyti mūsų darbuotojų duomenis;",
                    "teikti klientų aptarnavimo paslaugą;",
                    "auditui;",
                    "sužinoti lankytojų nuomonę apie Jūsų teikiamas paslaugas;",
                    "užtikrinti duomenų saugumą ir užkirstikelią sukčiavimui;gerinti paslaugų kokybę;",
                    "skelbti lankytojų atsiliepimus ir rekomendacijas;",
                    "vykdant įstatymuose numatytas pareigas ar teises;",
                    "užkirsti kelią piktnaudžiavimui Jūsų siūlomais produktais ir/ar paslaugomis;",
                    "siųsti  rinkodaros informaciją;",
                ],
            },
            {
                model: "info_usage_other",
                type: "addOption",
                label: "Enter other ways you use information",
                addLabel: "Enter",
                noTrans: true,
                default: [],
                inline: true,
            },
        ],
    },
    {
        name: "Direct Marketing",
        inputs: [
            {
                title:
                    "Do you use personal data to send marketing and promotional information?",
                model: "direct_marketing.use",
                type: "radio",
                default: "false",
                values: {
                    true: "Yes",
                    false: "No",
                },
            },
            {
                title: "How your user can unsubscribe?",
                model: "direct_marketing.unsubscribe",
                condition: (model) => model["direct_marketing.use"] === "true",
                type: "multiSelect",
                result: "object",
                rules: [rules.atLeastOne],
                default: {
                    via_preferences: false,
                    via_instructions: false,
                    via_email: false,
                    via_link: false,
                },
                values: {
                    via_preferences:
                        "User can access his settings in the account and update his preferences any time.",
                    via_instructions:
                        "User can follow the instructions in the send email and unsubscribe any time.",
                    via_email: {
                        label:
                            "User can contact you by the email provided and ask to opt out.",
                        inputs: [
                            {
                                label: "Enter the unsubscribe email",
                                model: "unsubscribe_email",
                                type: "text",
                                class: "th-mt-12 th-mb-4 col-md-6",
                                condition: (model) =>
                                    model.find((k) => k === "via_email"),
                                rules: [rules.required, rules.email],
                            },
                        ],
                    },
                    via_link:
                        "User can click on the link in the received email to unsubscribe.",
                },
            },
        ],
        validate: (model) => {
            if (isTrue(model["direct_marketing.use"])) {
                const atLeastOneSelected =
                    Object.values(model["direct_marketing.unsubscribe"]).filter(
                        (v) => v
                    ).length <= 0
                        ? translation.t("At least one has to be selected")
                        : "";

                if (atLeastOneSelected) return atLeastOneSelected;

                if (
                    model["direct_marketing.unsubscribe"].via_email &&
                    rules.email(
                        model["direct_marketing.unsubscribe"][
                            "unsubscribe_email"
                        ]
                    ) !== true
                )
                    return translation.t("Please enter unsubscribe email");
            }

            return null;
        },
    },
    {
        name: "Third Party",
        inputs: [
            {
                title: "To whom you share information?",
                model: "third_parties",
                type: "multiSelect",
                noTrans: true,
                default: [],
                values: [
                    "tarpininkams, kurie tvarko duomenis tam, kad sudaryti ir administruoti sutartis su klientais ir partneriais;",
                    "informacinių technologijų bendrovėms ir asmenims, kurie duomenis tvarko, kad užtikrintų informacinių sistemų kūrimą, tobulinimą ir palaikymą;",
                    "bendrovėms, kurios užtikrina pranešimų klientams siuntimą, teikia apsaugos ir kitas paslaugas;",
                    "buhalterinę apskaitą tvarkančioms įmonėms ir asmenims;",
                    "bendrovėms, tvarkančioms jungtines skolininkų rinkmenas ar administruojančioms skolas;",
                    "partneriams, dukterinėms kompanijoms;",
                    "kitiems asmenims Jūsų sutikimu, jei toks sutikimas gaunamas dėl konkretaus atvejo;",
                    "apsaugos įmonėms;",
                    "reklamos agentūroms;",
                    "prieglobos (hostingo) įmonėms;",
                ],
            },
            {
                model: "third_parties_other",
                type: "addOption",
                label:
                    "Enter other third party provides you share information with",
                addLabel: "Enter",
                default: [],
                inline: true,
            },
        ],
    },
    {
        name: "Cookies",
        alerts: [
            {
                component: () =>
                    import("@/components/DocumentGeneration/Alert.vue"),
                params: () => ({
                    title: "What is a cookie?",
                    subtitle:
                        "A cookie is a small text file placed onto your user's device that enables your website features and functionality. For example, cookies can enable you to identify your website's user's device and secure access to your website. Cookies also permit you to recognize users and avoid repetitive requests for the same information.",
                }),
            },
            {
                condition: (model) =>
                    isFalse(model["cookies.used"]) ||
                    model["cookies.policy_link"] === "dontuse",
                component: () =>
                    import("@/components/DocumentGeneration/Alert.vue"),
                params: () => ({
                    title:
                        "We strongly recommend that you include a Cookie Policy for your website",
                }),
            },
        ],
        inputs: [
            {
                title: "Do you use cookies and other tracking technologies?",
                model: "cookies.used",
                type: "radio",
                default: "true",
                values: {
                    true: "Yes",
                    false: "No",
                },
            },
            {
                condition: (model) => isTrue(model["cookies.used"]),
                title: "Please insert the link to your Cookie Policy",
                model: "cookies.policy_link",
                type: "projectDocument",
                values: {
                    dontuse: "I want to continue without one",
                },
                policyType: COOKIE_POLICY,
            },
            {
                condition: (model) =>
                    isTrue(model["cookies.used"]) &&
                    model["cookies.policy_link"] === "dontuse",
                model: "cookies.types",
                title: "What type of cookies do you use on your website?",
                type: "multiSelect",
                default: {
                    necessary: true,
                    functional: false,
                    performance: false,
                    targeted: false,
                },
                result: "object",
                values: {
                    necessary: {
                        label: "Necessary",
                        disabled: true,
                        tooltip:
                            "Strictly necessary cookies - usually these cookies are required for the operation of the website. They might help you to show the user the right information, customize his experience, and allow you to implement and maintain security features. Usually, without these cookies operation of the website is impossible or its functioning may be severely affected.",
                    },
                    functional: {
                        label: "Preferences",
                        tooltip:
                            "Functional cookies - usually these cookies are used to improve the functional performance of your website and make it easier for users to use. These cookies remember the settings selected by the users (for example, the settings of language and time zone). With the use of these cookies, the users may avoid changes of settings during each visit of the website.",
                    },
                    performance: {
                        label: "Statistics",
                        tooltip:
                            "Performance cookies - usually these cookies usually show if the user has visited your website before. The analytic cookies allow you to recognize and count the number of users of your website and see how such users navigate through your website.",
                    },
                    targeted: {
                        label: "Marketing",
                        tooltip:
                            "Marketing, targeting, and advertising cookies - usually these cookies record your visitor's visits to your website, the pages they have visited, and the links they have followed. This information is used to make the advertising displayed on it more relevant to your user's interests. The advertising cookies can let you know whether your user has already seen the specific advertisement or a certain type of advertisement.",
                    },
                },
            },
        ],
    },
    // {
    //     name: "Minors Information",
    //     inputs: [
    //         {
    //             title:
    //                 "Do you collect information from minors (under 18 years)?",
    //             model: "minors_information",
    //             type: "radio",
    //             default: "false",
    //             values: {
    //                 true: "Yes",
    //                 false: "No",
    //             },
    //         },
    //     ],
    // },
    {
        name: "Sensitive Information",
        inputs: [
            {
                title:
                    "Do you collect sensitive information such as racial or ethnic origin, political opinions, religious or philosophical beliefs, genetic data, biometric data, health data or data related a sexual orientation?",
                model: "sensitive_information",
                type: "radio",
                default: "false",
                values: {
                    true: "Yes",
                    false: "No",
                },
            },
        ],
    },
    {
        name: "Advertisement",
        alerts: [
            {
                component: () =>
                    import("@/components/DocumentGeneration/Alert.vue"),
                params: () => ({
                    title:
                        "Please select YES, even if your website has only the link with your own social media account (e.g. Facebook, Twitter, Instagram).",
                }),
            },
        ],
        inputs: [
            {
                title:
                    "Do you have advertisements or links of any third party on your website?",
                model: "third_party_links",
                type: "radio",
                default: "false",
                values: {
                    true: "Yes",
                    false: "No",
                },
            },
        ],
    },
    {
        name: "Retention",
        inputs: [
            {
                title: "How long you keep user's personal information?",
                model: "retention.main",
                type: "radio",
                allowClear: true,
                default: "while_service_provided",
                values: {
                    while_account_exists: "As long as user has an account?",
                    while_service_provided:
                        "As long as is necessary to provide services?",
                    months_after_termination:
                        "Particular time after termination of the account?",
                    months_after_abandonment:
                        "Particular time after users account was abandoned?",
                },
            },
            {
                label: "Enter the number of months",
                model: "retention.months_after_termination_period",
                type: "text",
                class: "th-mt-12 th-mb-4 col-md-6",
                condition: (model) =>
                    model["retention.main"] === "months_after_termination",
                rules: [rules.required],
            },
            {
                condition: (model) =>
                    model["retention.main"] === "months_after_abandonment",
                label: "Enter the number of months",
                model: "retention.months_after_abandonment_period",
                type: "text",
                class: "th-mt-12 th-mb-4 col-md-6",
                rules: [rules.required],
            },
            {
                model: "retention.other_cases",
                type: "addOption",
                addLabel: "Enter other options",
                default: [],
            },
        ],
        validate: (model) => {
            return model["retention.main"] || model["retention.other_cases"].length > 0
                ? ""
                : translation.t("At least one has to be selected or entered");
        },
    },
    {
        name: "Personal information security",
        alerts: [
            {
                component: () =>
                    import("@/components/DocumentGeneration/Alert.vue"),
                params: () => ({
                    title: "What is considered appropriate security measures?",
                    subtitle:
                        "Appropriate security measures are intended to protect personal information against a breach of security leading to the accidental or unlawful destruction, loss, alteration, unauthorised disclosure of, or access to, personal data transmitted, stored or otherwise processed.",
                }),
            },
        ],
        inputs: [
            {
                title:
                    "Have you implemented security measures designed to protect personal information you receive or collect?",
                model: "security",
                type: "radio",
                default: "false",
                values: {
                    true: "Yes",
                    false: "No",
                },
            },
            {
                condition: (model) => model["security"] !== "true",
                type: "custom",
                component: () =>
                    import(
                        "@/components/Documents/PrivacyPolicy/WarningSecurityMeasures.vue"
                    ),
            },
        ],
    },
];
